import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import successIcon from '@iconify/icons-solar/check-circle-linear';
import errorIcon from '@iconify/icons-solar/close-circle-linear';
import warningIcon from '@iconify/icons-solar/danger-triangle-linear';
import infoIcon from '@iconify/icons-solar/info-circle-linear';
import closeIcon from '@iconify/icons-solar/close-circle-linear';
import './feedback.css';
export type ToastType = 'success' | 'warning' | 'error' | 'info';
export interface ToastData { id: string; type: ToastType; title: string; message?: string; duration?: number; }
const icons = { success: successIcon, error: errorIcon, warning: warningIcon, info: infoIcon };
function ToastItem({toast,onDismiss}: {toast: ToastData;onDismiss:(id:string)=>void}) {
  const duration = toast.duration ?? (toast.type === 'error' ? 0 : 5000);
  const remaining = useRef(duration);
  const [hovered,setHovered] = useState(false); const [focused,setFocused] = useState(false);
  const [hidden,setHidden] = useState(document.hidden);
  useEffect(() => { const change = () => setHidden(document.hidden); document.addEventListener('visibilitychange',change); return () => document.removeEventListener('visibilitychange',change); }, []);
  useEffect(() => {
    if (!duration || hovered || focused || hidden) return;
    const start = Date.now();
    const timer = window.setTimeout(() => onDismiss(toast.id), remaining.current);
    return () => { window.clearTimeout(timer); remaining.current = Math.max(0, remaining.current - (Date.now() - start)); };
  }, [duration,hovered,focused,hidden,toast.id,onDismiss]);
  return <div className={`feedback-toast feedback-toast-${toast.type}`} onMouseEnter={()=>setHovered(true)} onMouseLeave={()=>setHovered(false)} onFocusCapture={()=>setFocused(true)} onBlurCapture={e=>{if(!e.currentTarget.contains(e.relatedTarget as Node))setFocused(false);}}>
    <Icon icon={icons[toast.type]} width={22} aria-hidden="true"/>
    <div role={toast.type==='error'?'alert':'status'} aria-atomic="true"><strong>{toast.title}</strong>{toast.message&&<p>{toast.message}</p>}</div>
    <button type="button" aria-label={`Dismiss ${toast.title}`} onClick={()=>onDismiss(toast.id)}><Icon icon={closeIcon} width={19}/></button>
  </div>;
}
export default function ToastContainer({toasts,onDismiss}: {toasts:ToastData[];onDismiss:(id:string)=>void}) {
  const [host] = useState(() => document.createElement('div'));
  const count = useRef(toasts.length);
  count.current = toasts.length;
  const syncHost = useRef<() => void>(() => {});
  useEffect(() => {
    host.className = 'toast-stack'; host.setAttribute('aria-label','Notifications');
    const layer = host as HTMLDivElement & { showPopover?: () => void; hidePopover?: () => void };
    if (layer.showPopover) host.setAttribute('popover','manual');
    let stack: HTMLDialogElement[] = Array.from(document.querySelectorAll<HTMLDialogElement>('dialog[open]'));
    const place = (records: MutationRecord[] = []) => {
      stack = stack.filter(d=>d.isConnected && d.open);
      records.forEach(r => { if (r.type === 'attributes' && r.target instanceof HTMLDialogElement && r.target.open) { stack = stack.filter(d=>d!==r.target); stack.push(r.target); } });
      document.querySelectorAll<HTMLDialogElement>('dialog[open]').forEach(d=>{if(!stack.includes(d))stack.push(d);});
      const forms = document.querySelectorAll<HTMLElement>('[role="dialog"][aria-modal="true"]:not(dialog)');
      const parent = stack[stack.length-1] || forms[forms.length-1] || document.body;
      if (host.parentElement !== parent) {
        layer.hidePopover?.();
        parent.appendChild(host);
      }
      if (count.current) layer.showPopover?.();
      else layer.hidePopover?.();
    };
    syncHost.current = place;
    place();
    const observer = new MutationObserver(place);
    observer.observe(document.body,{subtree:true,childList:true,attributes:true,attributeFilter:['open','aria-modal']});
    return () => {observer.disconnect();layer.hidePopover?.();host.remove();syncHost.current=()=>{};};
  }, [host]);
  useEffect(() => syncHost.current(), [toasts.length]);
  // Stay in the active dialog's accessible subtree; a manual popover escapes its clipping and transforms.
  return createPortal(toasts.map(t=><ToastItem key={t.id} toast={t} onDismiss={onDismiss}/>),host);
}
